import { computed } from 'mobx';
import { realDealFeatures as Features } from '@wix/premium-marketing-data/dist/src/marketing-data/real-deal/features';
import {
  Country,
  Experiments,
  PaymentCycle,
  PlanType,
  ProductFamily,
  Template,
} from 'enums';
import {
  AdvancedPlansFeature,
  AdvancedPlansFeatureSection,
  FeaturesGroupSection,
} from 'types';
import { isCycleOfMonths } from 'utils';
import { CyclesProps } from './Cycles';

export class CyclesController {
  constructor(private readonly props: CyclesProps) { }

  isBordred = (index: number): boolean =>
    index >= 1 &&
    index < this.props.rootStore.appStore.currentProduct.cycles.length;

  isCycleSelected = (cycleId: PaymentCycle) =>
    cycleId === this.props.rootStore.appStore.currentCycle;

  isAscendBundle({ currentProduct, currentCycle }): boolean {
    return (
      [PaymentCycle.YEAR2, PaymentCycle.YEAR3].includes(currentCycle) &&
      currentProduct.isAscendBundle
    );
  }
  isMonthlyDisabledInProduct = (feature: AdvancedPlansFeature) => {
    const { currentProduct } = this.props.rootStore.appStore;
    return (
      feature.includedInProducts.has(currentProduct.id.toString()) &&
      feature.disabledInMonthlyCycle
    );
  };

  missingFreeCouponsFeatures = () => {
    const { experiments } = this.props.rootStore.appStore.appContext;
    if (
      !(experiments[Experiments.EnableImmediateCouponsDeliverySpec] === 'true')
    ) {
      return [];
    }

    return [
      {
        translationKey:
          'premium-purchase-plan.cycle_selection.monthly_warning.social_media_marketing',
      },
      {
        translationKey:
          'premium-purchase-plan.cycle_selection.monthly_warning.email_marketing_plan',
      },
    ] as AdvancedPlansFeature[];
  };

  missingMonthlyInSections = (
    sectionsOrGroups: (AdvancedPlansFeatureSection | FeaturesGroupSection)[],
  ) => {
    return sectionsOrGroups
      ?.map(({ features: sectionFeatures }) =>
        sectionFeatures.filter(this.isMonthlyDisabledInProduct),
      )
      .reduce((acc, next) => [...acc, ...next], []);
  };

  getGridRowsConfig = () => {
    const {
      pageMode: { template },
      country,
    } = this.props.rootStore.appStore.appContext;
    const rowHeight =
      country === Country.BR
        ? '78px'
        : template === Template.CandyBar
          ? '66px'
          : '72px';
    return {
      '--grid-row-height': rowHeight,
      '--grid-rows-count': this.cycles.length,
    } as React.CSSProperties;
  };

  @computed
  get showDisclaimer() {
    const { currentProduct, currentCycle } = this.props.rootStore.appStore;
    return (
      currentProduct.productFamily !== ProductFamily.Enterprise &&
      currentProduct.productFamily !== ProductFamily.ConnectDomain &&
      isCycleOfMonths(currentCycle) &&
      !!this.missingFeatureList.length
    );
  }
  get isBusinessProduct() {
    const { currentProduct } = this.props.rootStore.appStore;
    return currentProduct.type === PlanType.Business;
  }

  @computed
  get missingFeatureList() {
    const {
      appContext: {
        features: websiteFeatures,
        businessFeatures,
        sections,
        websiteFeatureGroups,
        businessFeatureGroups,
        featureGroups,
        experiments,
      },
      isSunriseV2Enabled,
    } = this.props.rootStore.appStore;

    const features = this.isBusinessProduct
      ? businessFeatures
      : websiteFeatures;

    const groups = isSunriseV2Enabled
      ? featureGroups!
      : this.isBusinessProduct
        ? businessFeatureGroups
        : websiteFeatureGroups;

    const missingFeatures = features.filter(this.isMonthlyDisabledInProduct);
    const missingSectionFeatures = this.missingMonthlyInSections(sections);
    const missingGroupsFeatures =
      experiments[Experiments.FeaturesGroupingPlansGrid] === 'true' ||
        isSunriseV2Enabled
        ? this.missingMonthlyInSections(groups)
        : [];

    const missingFreeCouponsFeatures = this.missingFreeCouponsFeatures();

    return [
      ...missingFeatures,
      ...missingFreeCouponsFeatures,
      ...missingGroupsFeatures,
      ...missingSectionFeatures,
    ];
  }

  @computed
  get disclaimerKey(): string {
    const hasEmailVoucher = (product) =>
      this.getEmailYearlyAdvancedPlanFeature(product);

    const { currentProduct } = this.props.rootStore.appStore;
    const hasQuaeterly = currentProduct.cycles.find(
      (cycle) => cycle.cycleId === PaymentCycle.QUARTERLY,
    );
    return hasQuaeterly
      ? 'cycle_entry.quarterly_disclaimer'
      : hasEmailVoucher(currentProduct)
        ? 'cycle_entry.monthly_disclaimer.with_mailbox_voucher'
        : 'cycle_entry.monthly_disclaimer';
  }

  private getEmailYearlyAdvancedPlanFeature(currentProduct) {
    const { features } = this.props.rootStore.appStore.appContext;
    return features
      .filter((f) => {
        return f.includedInProducts.has(`${currentProduct.id}`);
      })
      .find((f) => {
        return f.translationKey === Features.free_business_email_1_year.code;
      });
  }

  @computed
  get cycles() {
    const { currentProduct, setCurrentCycle } = this.props.rootStore.appStore;
    return currentProduct.cycles.map(
      (
        {
          cycleId,
          fullTermActualPrice,
          fullTermDisplayPrice,
          fullTermDisplaySavings,
          monthlyActualPrice,
          isCycleDisabledInProduct,
          monthlyFullPrice,
          firstPeriodPriceMonthly,
          firstPeriodPrice,
          fullTermPriceBeforeTax,
        },
        index,
      ) => {
        return {
          cycleDetails: {
            cycleId,
            fullTermActualPrice,
            fullTermDisplayPrice,
            fullTermDisplaySavings,
            monthlyActualPrice,
            isCycleDisabledInProduct,
            monthlyFullPrice,
            firstPeriodPriceMonthly,
            firstPeriodPrice,
            fullTermPriceBeforeTax,
            isAscendBundle: this.isAscendBundle({
              currentProduct,
              currentCycle: cycleId,
            }),
          },
          cellProps: {
            disabled: isCycleDisabledInProduct,
            onClick: () => setCurrentCycle({ cycle: cycleId }),
            bordered: this.isBordred(index),
            dataHook: `cycle-${cycleId}`,
          },
        };
      },
    );
  }
  @computed
  get maxPossibleCycle() {
    return this.cycles[0]?.cycleDetails?.cycleId || PaymentCycle.MONTHLY;
  }
}
